import { routeTo, finish, openModal } from '../../../../core/actions';
import { registerFlow } from '../../../../core/flow-register';
import FlowHeader from '../../../../components/FlowHeader/FlowHeader';
import FooterSite from '../../../../components/FooterSite/FooterSite';
import BaseLayout from '../../../../components/BaseLayout/BaseLayout';
import InternalAuthenticator from '../../../../components/BaseLayout/InternalAuthenticator';
import SummitRewards from '../../../../components/BaseLayout/SummitRewards';
import AddressValidationModal from '../../../../components/AddressValidationModal/AddressValidationModal';
import RPSPlansModal from '../../../../components/RPSPlansModal/RPSPlansModal';
import RPSPlansBySSNModal from '../../../../components/RPSPlansBySSNModal/RPSPlansBySSNModal';
import RPSOCRNewFundsModal from '../../../../components/RPSOCRNewFundsModal/RPSOCRNewFundsModal';
import RPSOneCallRolloverScreen from '../../../../screens/RPSOneCallRolloverScreen/RPSOneCallRolloverScreen';
import RPSOneCallRolloverLandingScreen from '../../../../screens/RPSOneCallRolloverLandingScreen/RPSOneCallRolloverLandingScreen';
import RPSOneCallRolloverAdditionalFundsScreen from '../../../../screens/RPSOneCallRolloverAdditionalFundsScreen/RPSOneCallRolloverAdditionalFundsScreen';
import RPSOneCallRolloverIRAConfirmationScreen from '../../../../screens/RPSOneCallRolloverIRAConfirmationScreen/RPSOneCallRolloverIRAConfirmationScreen';
import { FlowNode } from '../../../../core/types/flow';
import { contactTextFn } from '../../../shared/utils';

import {
  chosenAddressValidation,
  isValidSSN,
  isValidDob,
  isValidBeneorSucessorDob,
  meetsMinimumInvestment,
  compose,
  chose,
  chosenOCRFundSection,
  isNewFundPresent,
  hasMultipleOCRPlans,
  noSSNInRPSPlans,
  taxWithholdingValidation
} from '../../../shared/testers';

import {
  commitRPSPlans,
  makeLoadOCROMNIData,
  loadOCRExistingAgreementsAndCustomerData,
  createRPSOCRApplication,
  createOCRPDF,
  loadOCRRolloverAmount
} from '../loaders';
import {
  firstnameValidation,
  middlenameValidation,
  lastnameValidation
} from '../../../shared/validations';
import { rpsAccountTypes } from '../../shared/utils';

const nodes: Array<FlowNode> = [
  {
    route: 'rpsOneCallRolloverTRAC',
    screen: RPSOneCallRolloverLandingScreen,
    load: makeLoadOCROMNIData,
    postLoad: {
      cases: [
        [hasMultipleOCRPlans, openModal(RPSPlansModal)],
        [noSSNInRPSPlans, openModal(RPSPlansBySSNModal)]
      ],
      default: finish()
    },
    form: [
      { name: 'rpsSSN', validation: isValidSSN },
      { name: 'disclosure1', type: 'radio', required: true },
      { name: 'disclosure2', type: 'radio', required: true }
    ],
    commit: commitRPSPlans,
    next: {
      cases: [
        [hasMultipleOCRPlans, openModal(RPSPlansModal)],
        [chose('landing'), routeTo('rpsOneCallRolloverSecondWizard')]
      ],
      default: finish()
    },
    flowProps: {
      accountType: rpsAccountTypes.TRAC
    }
  },
  {
    route: 'rpsOneCallRolloverSecondWizard',
    screen: RPSOneCallRolloverAdditionalFundsScreen,
    load: loadOCRExistingAgreementsAndCustomerData,
    form: [
      { name: 'companyStock', type: 'radio', required: true },
      { name: 'rothHoldingsAfterTax', type: 'radio', required: true }
    ],
    next: {
      default: routeTo('form')
    },
    flowProps: {
      analytics: {
        pageName: 'rpsOneCallRolloverSecondWizard'
      }
    }
  },
  {
    route: 'form',
    screen: RPSOneCallRolloverScreen,
    load: loadOCRRolloverAmount,
    commit: createRPSOCRApplication,
    form: [
      { name: 'firstName', required: true },
      { name: 'lastName', required: true },
      { name: 'dob', required: true, validation: isValidDob },
      { name: 'citizenship', required: true },
      { name: 'ssn', required: true, validation: isValidSSN },
      { name: 'amount', required: true, validation: meetsMinimumInvestment },
      { name: 'residentialStreet1', required: true },
      { name: 'residentialStreet2' },
      { name: 'residentialZip', required: true },
      { name: 'residentialCity', required: true },
      { name: 'residentialState', required: true },
      { name: 'mailingStreet1', required: true },
      { name: 'mailingStreet2' },
      { name: 'mailingZip', required: true },
      { name: 'mailingCity', required: true },
      { name: 'mailingState', required: true },
      { name: 'primaryEmailAddress', required: true },
      { name: 'phone1' },
      { name: 'phone2' },
      { name: 'phone3' },
      { name: 'mailingAddressDifferent', type: 'checkbox' },
      { name: 'useResidentialAddress', required: true },
      { name: 'useMailingAddress', required: true },
      { name: 'primaryBeneficiaryInfo', type: 'checkbox' },
      { name: 'primaryBeneCategoryType', required: true },
      {
        name: 'primaryBeneFirstName',
        required: true,
        validation: firstnameValidation
      },
      { name: 'primaryBeneMiddleName', validation: middlenameValidation },
      {
        name: 'primaryBeneLastName',
        required: true,
        validation: lastnameValidation
      },
      {
        name: 'primaryBeneEntityName',
        required: true
      },
      { name: 'primaryBeneDob', validation: isValidBeneorSucessorDob },
      { name: 'primaryBeneSsn', validation: isValidSSN },
      { name: 'primaryBeneRelationship', required: true },
      { name: 'primaryBenePercentDistribution', required: true },
      { name: 'secondaryBeneficiaryInfo', type: 'checkbox' },
      { name: 'secondaryBeneCategoryType', required: true },
      {
        name: 'secondaryBeneFirstName',
        required: true,
        validation: firstnameValidation
      },
      {
        name: 'secondaryBeneMiddleName',
        validation: middlenameValidation
      },
      {
        name: 'secondaryBeneLastName',
        required: true,
        validation: lastnameValidation
      },
      {
        name: 'secondaryBeneEntityName',
        required: true
      },
      { name: 'secondaryBeneDob', validation: isValidBeneorSucessorDob },
      { name: 'secondaryBeneSsn', validation: isValidSSN },
      { name: 'secondaryBeneRelationship', required: true },
      { name: 'secondaryBenePercentDistribution', required: true },
      { name: 'rolloverAmount' },
      { name: 'rolloverAmountOption' },
      { name: 'fundingType', required: true },
      { name: 'deliveryType' },
      { name: 'paperlessWithdrawls' },
      { name: 'participantStatus' },
      { name: 'participantAge' },
      { name: 'brokerageAssets' },
      { name: 'participantLoanBalance' },
      { name: 'participantCashDistribution' },
      { name: 'distributionStatus' },
      { name: 'rolloverOptionsNotice' },
      { name: 'existingAgreementType' },
      {
        name: 'taxWithholdingOption'
      },
      {
        name: 'taxWithholdingPercent',
        required: true,
        validation: taxWithholdingValidation
      }
    ],
    next: {
      cases: [
        [chosenAddressValidation, openModal(AddressValidationModal)],
        [
          compose(chosenOCRFundSection, isNewFundPresent),
          openModal(RPSOCRNewFundsModal)
        ],
        [chose('ocrSubmit'), routeTo('confirmation')]
      ],
      default: finish()
    },
    flowProps: {
      isOCRTracApp: true
    }
  },
  {
    route: 'confirmation',
    screen: RPSOneCallRolloverIRAConfirmationScreen,
    commit: createOCRPDF,
    next: {
      default: finish()
    }
  }
];

export default registerFlow({
  namespace: 'front-office/rps-trac',
  nodes,
  start: 'rpsOneCallRolloverTRAC',
  pageDecorators: [BaseLayout, InternalAuthenticator, SummitRewards],
  flowProps: {
    getContactText: contactTextFn(),
    header: FlowHeader,
    footer: FooterSite
  }
});
